import React, {
	useEffect,
	useState,
	useMemo,
	useCallback,
	useRef,
	type SyntheticEvent,
} from 'react';
import { styled, keyframes } from '@compiled/react';
import noop from 'lodash/noop';
import { Transition } from 'react-transition-group';
import { Box, xcss } from '@atlaskit/primitives';
import CustomizeIcon from '@atlaskit/icon/core/migration/customize--preferences';
import LockLockedIcon from '@atlaskit/icon/core/migration/lock-locked--lock-filled';
import LockUnlockedIcon from '@atlaskit/icon/core/migration/lock-unlocked--unlock-filled';
import SidebarExpandIcon from '@atlaskit/icon/core/sidebar-expand';
import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import { ModalTransition } from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import Tooltip from '@atlaskit/tooltip';
import { IconButton } from '@atlaskit/button/new';
import { GraphLayout } from '@atlassian/jira-assets-graph-wrappers/src/ui/graph-layout/index.tsx';
import { GraphWrapper } from '@atlassian/jira-assets-graph-wrappers/src/ui/graph-wrapper/index.tsx';
import { GraphActionsWrapper } from '@atlassian/jira-assets-graph-wrappers/src/ui/graph-actions-wrapper/index.tsx';
import { ObjectDetailsRenderingContext } from '@atlassian/jira-assets-shared-types/src/common/types/object-details-rendering-context.tsx';
import { EmbeddedObjectView } from '@atlassian/jira-assets-embedded-object-view/src/ui/index.tsx';
import { useObjectSearchStoreState } from '@atlassian/jira-servicedesk-insight-object-search-store/src/controllers/store/index.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { useWorkspaceContext } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/controllers/workspace-context/index.tsx';
import { graphModalExperience } from '@atlassian/jira-servicedesk-insight-experiences/src/index.tsx';
import {
	ShowObjectGraphDetailsIcon,
	HideObjectGraphDetailsIcon,
} from '@atlassian/jira-servicedesk-insight-glyphs/src/index.tsx';
import { AsyncLazyForceGraph as AsyncForceGraph } from '@atlassian/jira-assets-graphs-force-graph/src/async.tsx';
import { NODE_TYPE } from '@atlassian/jira-assets-graphs-force-graph/src/common/constants.tsx';
import type { EChartsEvent } from '@atlassian/jira-assets-graphs-force-graph/src/common/types.tsx';
import { usePushingSidebar } from '@atlassian/jira-assets-graphs-sidebar/src/controllers/index.tsx';
import { useObjectGraphService } from '@atlassian/jira-servicedesk-insight-object-services/src/services/object-graph/index.tsx';
import {
	type CmdbObjectId,
	toCmdbObjectId,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { GraphHeader } from '@atlassian/jira-assets-graph-header/src/index.tsx';
import { GraphPanelSplitter } from '@atlassian/jira-assets-graph-panel-splitter/src/ui/graph-panel-splitter/index.tsx';
import { GRAPH_NODE_TYPES } from '../common/types.tsx';
import { useObjectGraphConfiguration } from '../controllers/configuration/index.tsx';
import { useSelectedNode } from '../controllers/selected-node/index.tsx';
import { OBJECT_GRAPH_DIALOG_WIDTH, OBJECT_GRAPH_DIALOG_HEIGHT } from '../common/constants.tsx';
import { GraphConfigurationActionPanel } from './graph-config-panel/dropdown-panel/index.tsx';
import { ReferenceDepthConfigurationInputs } from './graph-config-panel/reference-depth-inputs/index.tsx';
import { ReferenceTypeToggleList } from './graph-config-panel/reference-types-toggle-list/index.tsx';
import { GraphError } from './graph-error/index.tsx';
import messages from './messages.tsx';
import { ObjectTypeView } from './object-type-view/index.tsx';
import type { AssetsObjectGraphOrNullProps, AssetsObjectGraphProps } from './types.tsx';
import {
	getNormalizedGraphData,
	getFilteredEdgesAndNodes,
	getConnectedNodesFromParent,
} from './utils.tsx';

const OBJECT_GRAPH_FADEIN_ANIMATION_DELAY = 500;

export const AssetsObjectGraph = ({
	objectId,
	objectTypeId,
	onClose,
	source,
	disableResizePanel = false,
}: AssetsObjectGraphProps) => {
	const [selectedNode, { setSelectedObjectNode, setSelectedObjectTypeNode }] =
		useSelectedNode(objectId);
	const { formatMessage } = useIntl();
	const { workspaceId } = useWorkspaceContext();
	const [sidebarState, { toggleSidebar, setSidebarWidth }] = usePushingSidebar({ width: 560 });

	const [isConfigPanelOpen, setIsConfigPanelOpen] = useState<boolean>(false);
	const [isGraphLayoutLocked, setIsGraphLayoutLocked] = useState<boolean>(true);
	const hasUpdatedObjectView = useRef(false);
	const singleClickedNodeContext = useRef<null | EChartsEvent>(null);
	const [selectedObjectIdToDisplayObjectGraph, setSelectedObjectIdToDisplayObjectGraph] =
		useState<CmdbObjectId>(objectId);

	const [{ data: objectGraphData, loading: graphLoading, error: graphError }, fetchObjectGraph] =
		useObjectGraphService();

	const [
		{ referenceTypes, maxOfSameType, referenceDepth },
		{ updateReferenceDepth, updateMaxOfSameType, updateReferenceTypes },
	] = useObjectGraphConfiguration(objectGraphData, objectTypeId);
	const [, { refreshSearch }] = useObjectSearchStoreState();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fetchGraphData = useCallback(
		(objId: string) => {
			if (workspaceId) {
				fetchObjectGraph({
					workspaceId,
					objectId: toCmdbObjectId(objId),
					depth: referenceDepth,
					maxOfSameType,
				});
			}
		},
		[fetchObjectGraph, maxOfSameType, referenceDepth, workspaceId],
	);

	const transformedGraphData = useMemo(() => {
		// the initial graph data response goes through a series of transformations before it renders:
		// 1. we filter the nodes & edges based on the currently selected reference types in the object graph configuration panel
		// 2. we only show nodes that have a connection (either direct or indirect) to the currently selected object based on that filtering
		// 3. we then transform the result of that into a data shape the force graph library expects
		const filteredBySelectedReferenceTypes = getFilteredEdgesAndNodes(
			objectGraphData,
			referenceTypes,
			selectedNode.referenceId,
		);
		const nodesConnectedToSelectedNode = getConnectedNodesFromParent(
			filteredBySelectedReferenceTypes,
			selectedNode.referenceId,
		);
		return getNormalizedGraphData(nodesConnectedToSelectedNode);
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [objectGraphData, referenceTypes]); // in this case we do want to explicity exclude the reference id here

	useEffect(() => {
		fetchGraphData(objectId);
	}, [fetchGraphData, fetchObjectGraph, objectId, workspaceId]);

	const onNodeClicked = useCallback(
		(selectedObjectData: EChartsEvent) => {
			singleClickedNodeContext.current = selectedObjectData;

			const { data, dataType } = selectedObjectData;
			const { context } = data;

			if (dataType === NODE_TYPE && context) {
				switch (context.type) {
					case GRAPH_NODE_TYPES.OBJECT:
						setSelectedObjectNode(context.id);
						break;
					case GRAPH_NODE_TYPES.OBJECT_TYPE:
						setSelectedObjectTypeNode(context);
						break;
					default:
						break;
				}
				fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'insightObjectGraphItem', {
					source,
				});
			}
		},
		[createAnalyticsEvent, setSelectedObjectNode, setSelectedObjectTypeNode, source],
	);

	const onNodeDoubleClicked = useCallback(
		(selectedObjectData: EChartsEvent) => {
			const { data, dataType } = selectedObjectData;
			const { context } = data;
			if (dataType === NODE_TYPE && context?.type === GRAPH_NODE_TYPES.OBJECT) {
				setSelectedObjectIdToDisplayObjectGraph(toCmdbObjectId(context.id));
				fetchGraphData(context.id);
			}
		},
		[fetchGraphData],
	);

	const onToggleLockedLayout = () => {
		const shouldLockGraph = !isGraphLayoutLocked;
		setIsGraphLayoutLocked(shouldLockGraph);
	};

	const HideObjectGraphDetailsIconComponent = isVisualRefreshEnabled()
		? SidebarExpandIcon
		: HideObjectGraphDetailsIcon;
	const ShowObjectGraphDetailsIconComponent = isVisualRefreshEnabled()
		? PanelRightIcon
		: ShowObjectGraphDetailsIcon;

	const renderGraphError = () => {
		graphModalExperience.failure();
		return <GraphError />;
	};
	const renderForceGraph = () => (
		<AsyncForceGraph
			graphData={transformedGraphData}
			onNodeClick={onNodeClicked}
			onNodeDoubleClick={onNodeDoubleClicked}
			defaultSelectedNodeId={selectedNode.nodeId}
			isLayoutLocked={isGraphLayoutLocked}
		/>
	);

	const content = (
		<>
			<GraphHeader
				title={formatMessage(messages.title)}
				onClose={onClose}
				toggleGraphSideBarAction={
					<IconButton
						icon={
							sidebarState.isOpen
								? HideObjectGraphDetailsIconComponent
								: ShowObjectGraphDetailsIconComponent
						}
						appearance="subtle"
						onClick={(_: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'insightObjectGraphToggleDetailsPanel', {
								action: sidebarState.isOpen ? 'hiding' : 'showing',
								source,
							});
							if (isConfigPanelOpen) {
								setIsConfigPanelOpen(() => false);
							}
							toggleSidebar();
						}}
						testId="assets-object-graph.ui.graph-toggle-button"
						label={formatMessage(sidebarState.isOpen ? messages.hideDetail : messages.showDetail)}
					/>
				}
			/>
			<GraphLayout>
				<GraphWrapper>
					{/*
					 * Using Transition to delay the unmounting of the Spinner.
					 * We need to do this because the FadeIn around the AsyncForceGraph has a built in animation delay for fixing a visual bug.
					 * Adding the delay here compensates for the one inside FadeIn and prevents a flash of 'nothing' being rendered momentarily.
					 */}
					<Transition
						timeout={{ exit: OBJECT_GRAPH_FADEIN_ANIMATION_DELAY }}
						in={graphLoading}
						unmountOnExit
					>
						<Box xcss={spinnerWrapperStyles}>
							<Spinner size="large" />
						</Box>
					</Transition>

					{!graphLoading && <FadeIn>{graphError ? renderGraphError() : renderForceGraph()}</FadeIn>}

					<GraphActionsWrapper>
						<GraphConfigurationActionPanel
							isConfigPanelOpen={isConfigPanelOpen}
							onOpenChange={(isOpen) => {
								// Only handle open events here. Close events are handled on the wrapper of this dialog because
								// otherwise clicks on the reference depth dropdown are considered "outside" this panel, and cause
								// the panel to close instead of updating the reference depth.
								isOpen && setIsConfigPanelOpen(true);
							}}
							menuPosition={sidebarState.isOpen ? 'left' : 'right'}
							renderActionButton={({ triggerRef, ...triggerProps }) => (
								<Tooltip
									content={formatMessage(
										isConfigPanelOpen
											? messages.hideGraphConfigPanel
											: messages.showGraphConfigPanel,
									)}
									position="top"
								>
									<IconButton
										// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
										ref={triggerRef as React.Ref<HTMLButtonElement>}
										appearance="subtle"
										icon={CustomizeIcon}
										onClick={(
											event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>,
										) => {
											if (isConfigPanelOpen) {
												setIsConfigPanelOpen(false);
											}
											triggerProps.onClick?.(event);
										}}
										label={formatMessage(
											isConfigPanelOpen
												? messages.hideGraphConfigPanel
												: messages.showGraphConfigPanel,
										)}
										testId="assets-object-graph.ui.graph-config-button"
									/>
								</Tooltip>
							)}
						>
							<Box xcss={configPanelHeaderStyles} as="h2">
								{formatMessage(messages.graphConfigPanelHeader)}
							</Box>
							<ReferenceDepthConfigurationInputs
								maxOfSameType={maxOfSameType}
								referenceDepth={referenceDepth}
								onUpdateReferenceDepth={updateReferenceDepth}
								onUpdateMaxOfSameType={updateMaxOfSameType}
							/>
							{referenceTypes.length > 0 ? (
								<>
									<Box xcss={referenceTypeListHeaderStyles} as="h3">
										{formatMessage(messages.referenceTypesHeader)}
									</Box>
									<ReferenceTypeToggleList
										referenceTypes={referenceTypes}
										onChange={updateReferenceTypes}
									/>
								</>
							) : null}
						</GraphConfigurationActionPanel>
						<Tooltip
							content={formatMessage(
								isGraphLayoutLocked ? messages.unlockLayout : messages.lockLayout,
							)}
							position="top"
						>
							<IconButton
								appearance="subtle"
								icon={isGraphLayoutLocked ? LockLockedIcon : LockUnlockedIcon}
								onClick={onToggleLockedLayout}
								isSelected={isGraphLayoutLocked}
								label={formatMessage(
									isGraphLayoutLocked ? messages.unlockLayout : messages.lockLayout,
								)}
							/>
						</Tooltip>
					</GraphActionsWrapper>
				</GraphWrapper>

				<GraphPanelSplitter
					isOpen={sidebarState.isOpen}
					sidebarWidth={sidebarState.sidebarWidth}
					setSidebarWidth={setSidebarWidth}
					disableResizePanel={disableResizePanel}
				>
					<>
						{selectedNode.type === GRAPH_NODE_TYPES.OBJECT && workspaceId && (
							<EmbeddedObjectView
								context={ObjectDetailsRenderingContext.ObjectGraph}
								objectId={selectedNode.referenceId}
								workspaceId={workspaceId}
								onUpdate={() => {
									hasUpdatedObjectView.current = true;
									refreshSearch();
								}}
								onDelete={noop}
								onClone={noop}
								redirectOnDelete={false}
								showFullBreadcrumbTrail={false}
								onFinishLoading={() => {
									if (hasUpdatedObjectView.current) {
										let objectIdToUpdate = selectedObjectIdToDisplayObjectGraph;
										if (singleClickedNodeContext.current?.data?.context) {
											objectIdToUpdate = toCmdbObjectId(
												singleClickedNodeContext.current.data.context.id,
											);
											setSelectedObjectIdToDisplayObjectGraph(objectIdToUpdate);
										}
										fetchGraphData(objectIdToUpdate);
									}
									hasUpdatedObjectView.current = false;
									singleClickedNodeContext.current = null;
								}}
							/>
						)}
						{selectedNode.type === GRAPH_NODE_TYPES.OBJECT_TYPE && (
							<ObjectTypeView objectTypeNode={selectedNode} />
						)}
					</>
				</GraphPanelSplitter>
			</GraphLayout>
		</>
	);

	return (
		<Box
			xcss={wrapperStyles}
			onClick={() => setIsConfigPanelOpen(false)}
			role="presentation"
			tabIndex={-1}
			onKeyDown={noop}
		>
			{content}
		</Box>
	);
};

export const AssetsObjectGraphOrNull = (
	props: AssetsObjectGraphOrNullProps | AssetsObjectGraphProps,
) => {
	if (props.objectId !== undefined && props.objectTypeId !== undefined) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return <AssetsObjectGraph {...(props as AssetsObjectGraphProps)} />;
	}
	return null;
};
export const AssetsObjectGraphModal = ({
	objectId,
	objectTypeId,
	onClose,
	source,
	testId,
	isObjectGraphOpen,
	disableResizePanel,
}: AssetsObjectGraphOrNullProps) => (
	<ModalTransition>
		{isObjectGraphOpen && (
			<JSErrorBoundary id="AssetsObjectGraph" packageName="jiraAssetsObjectGraph" fallback="flag">
				<ShortcutScope>
					<ModalDialog
						width={OBJECT_GRAPH_DIALOG_WIDTH}
						height={OBJECT_GRAPH_DIALOG_HEIGHT}
						onClose={onClose}
						autoFocus={false}
						stackIndex={1}
						testId={testId}
						messageId="assets-object-graph.ui.modal-dialog"
						messageType="transactional"
					>
						<AssetsObjectGraphOrNull
							objectId={objectId}
							objectTypeId={objectTypeId}
							onClose={onClose}
							source={source}
							isObjectGraphOpen={isObjectGraphOpen}
							disableResizePanel={disableResizePanel}
						/>
					</ModalDialog>
				</ShortcutScope>
			</JSErrorBoundary>
		)}
	</ModalTransition>
);

export default AssetsObjectGraphModal;

const wrapperStyles = xcss({
	height: 'calc(100% - 72px)',
	minHeight: '80px',
});

const spinnerWrapperStyles = xcss({
	/* Using position absolute because the SpinnerWrapper and graph get rendered at the same time momentarily. */
	/* This prevents the spinner from moving the graph position while they're both rendered */
	position: 'absolute',
	width: '100%',
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const fadeIn = keyframes({
	from: {
		opacity: 0,
	},
	to: {
		opacity: 1,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FadeIn = styled.div({
	opacity: 0,
	height: '100%',
	width: '100%',
	animationName: fadeIn,
	animationIterationCount: 1,
	/** We have a higher than normal animation delay here to prevent showing nodes flying around while the initial layout is calculated */
	animationDelay: `${OBJECT_GRAPH_FADEIN_ANIMATION_DELAY}ms`,
	animationDuration: '250ms',
	animationFillMode: 'forwards',
});

const configPanelHeaderStyles = xcss({
	marginBottom: 'space.300',
});

const referenceTypeListHeaderStyles = xcss({
	marginTop: 'space.300',
});
